import type { Document } from "photoshop/dom/Document";
import { v4 as uuidv4 } from "uuid";
import { notify } from "../api/photoshop";
import { useGlobalStore } from "../globalStore";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { base64ToUint8Array, createBase64FromRawPixels } from "../utils/jimp";
import { isCropToolActive } from "./shared/check-crop-tool";
import { fetchPostCancelInferenceJob } from "./shared/fetch-inference-job";
import { getTargetEditorImage } from "./shared/target-editor-image";

const TOOL_NAME = "Upscale Image";

const callUpscaleImageNewDocument = async (
	params: {
		prompt: string;
		seed: number;
		upscale: number;
		edmSteps: number;
		blur: number;
		userId: number;
	},
	messageId: string,
) => {
	let success = false;
	if (isCropToolActive()) {
		await notify("Cannot generate images while the Crop tool is active.");
		return success;
	}
	await photoshop.core.executeAsModal(
		async (executionContext) => {
			const doc = photoshop.app.activeDocument;

			if (!doc) {
				await notify("Open a PSD file first");
				return;
			}
			await doc.suspendHistory(async () => {
				let copiedLayer = null;
				let newDoc: Document | null = null;
				try {
					if (
						doc.mode !== "RGBColorMode" ||
						doc.bitsPerChannel !== "bitDepth8"
					) {
						await notify("Image mode must be RGB Color, 8bits/Channel");
						return;
					}

					if (doc.width > 1970 || doc.height > 1970) {
						await notify("The maximum document size per side is 1970px.");
						return;
					}

					const docAspectRatio =
						Math.max(doc.width, doc.height) / Math.min(doc.width, doc.height);
					if (docAspectRatio > 10 / 3) {
						await notify(
							"The image aspect ratio must not exceed 10:3. Please adjust the image size.",
						);
						return;
					}

					newDoc = await doc.duplicate(`(Upscaled)${doc.name}`);

					if (!newDoc) {
						await notify("Failed to create new document");
						return;
					}

					const newWidth = doc.width * 2;
					const newHeight = doc.height * 2;

					await newDoc.resizeImage(newWidth, newHeight);
					await newDoc.flatten();
					const flatLayer = newDoc.layers[0];
					copiedLayer = await flatLayer.duplicate();
					if (!copiedLayer) {
						await notify("Failed to duplicate layer");
						return;
					}
					copiedLayer.name = "Processing...";
					copiedLayer.selected = true;
					const { imageData } = await photoshop.imaging.getPixels({
						documentID: newDoc.id,
						layerID: copiedLayer.id,
					});

					const pixelData = await imageData.getData({});

					const imageBase64 = await createBase64FromRawPixels(
						pixelData as Uint8Array,
						newWidth,
						newHeight,
					);

					const data = {
						base64: imageBase64,
						prompt: params.prompt,
						seed: params.seed,
						upscale: params.upscale,
						edm_steps: params.edmSteps,
						blur: params.blur,
						user_id: params.userId,
						plugin_version: useGlobalStore.getState().version,
					};

					const result = await fetchUpscaleImage(
						data,
						executionContext,
						messageId,
					);
					if (!result) {
						imageData.dispose();
						return;
					}

					const { width, height, rawPixels } = base64ToUint8Array(result);

					const uint8Array = new Uint8Array(rawPixels);
					const resultImageData =
						await photoshop.imaging.createImageDataFromBuffer(uint8Array, {
							width,
							height,
							components: 4,
							colorSpace: "RGB",
						});

					const newLayer = await newDoc.createPixelLayer({
						name: TOOL_NAME,
					});

					if (!newLayer) {
						resultImageData.dispose();
						imageData.dispose();
						throw new Error("Failed to create layer");
					}

					await photoshop.imaging.putPixels({
						documentID: newDoc.id,
						layerID: newLayer.id,
						imageData: resultImageData,
						targetBounds: {
							left: 0,
							top: 0,
							right: width,
							bottom: height,
						},
						replace: true,
					});

					await newLayer.scale(
						(newWidth / width) * 100,
						(newHeight / height) * 100,
						photoshop.constants.AnchorPosition.TOPLEFT,
					);

					resultImageData.dispose();
					imageData.dispose();
					success = true;
				} catch (err) {
					console.error(err);
					await notify(`${err}`);
				} finally {
					if (copiedLayer) {
						copiedLayer.delete();
					}

					if (!success && newDoc) {
						setTimeout(async () => {
							await photoshop.core.executeAsModal(
								async () => {
									newDoc?.closeWithoutSaving();
								},
								{
									commandName: "close document",
								},
							);
						}, 500);
					}
					if (success && newDoc) {
						const width = newDoc.width;
						const height = newDoc.height;
						postToRenderer([
							{
								key: "documentInfoChanged",
								id: uuidv4(),
								values: {
									width,
									height,
								},
							},
						]);
					}
				}
			}, TOOL_NAME);
		},
		{
			commandName: TOOL_NAME,
		},
	);
	return success;
};

const callUpscaleImage = async (
	params: {
		prompt: string;
		seed: number;
		upscale: number;
		edmSteps: number;
		blur: number;
		newDocument: boolean;
		userId: number;
	},
	messageId: string,
) => {
	if (params.newDocument) {
		return callUpscaleImageNewDocument(params, messageId);
	}

	const { success } = await getTargetEditorImage({
		commandName: TOOL_NAME,
		resultLayerName: TOOL_NAME,
		requireSolidSelection: true,
		maxSide: undefined,
		processImage: async ({ imageBase64, selBounds, executionContext }) => {
			if (selBounds.width > 3940 || selBounds.height > 3940) {
				await notify("The maximum size per side is 3940px.");
				return false;
			}

			const selAspectRatio =
				Math.max(selBounds.width, selBounds.height) /
				Math.min(selBounds.width, selBounds.height);
			if (selAspectRatio > 10 / 3) {
				await notify(
					"The image aspect ratio must not exceed 10:3. Please adjust the selection size.",
				);
				return false;
			}

			const data = {
				base64: imageBase64,
				prompt: params.prompt,
				seed: params.seed,
				upscale: params.upscale,
				edm_steps: params.edmSteps,
				blur: params.blur,
				user_id: params.userId,
				plugin_version: useGlobalStore.getState().version,
			};

			return fetchUpscaleImage(data, executionContext, messageId);
		},
	});
	return success;
};

export type InferenceParams = {
	base64: string;
	prompt: string;
	seed: number;
	upscale: number;
	edm_steps: number;
	blur: number;
	user_id: number;
	plugin_version: string;
};

export async function fetchUpscaleImage(
	params: InferenceParams,
	executionContext: any,
	messageId: string,
): Promise<string | false> {
	return fetchPostCancelInferenceJob(
		params,
		executionContext,
		messageId,
		"/image-upscale",
	);
}

export function useListenUpscaleImageMessage() {
	useListenFromRenderer([
		{
			key: "upscaleImage",
			onMessage: async (message) => {
				const success = await callUpscaleImage(message.values, message.id);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
